import { API_ERROR_CONFIG, type TRPCErrorCode } from "@platform/shared/errors";
import type { CaptureResult } from "posthog-js";
import { posthog } from "posthog-js";

/**
 * Exception messages/patterns to suppress from error tracking. These are expected application errors (auth, not found,
 * rate limits, etc.) that would create noise in PostHog without providing actionable insights, since they are often
 * handled gracefully in the UI and don't indicate bugs in the code.
 */
const IGNORED_EXCEPTION_MESSAGES = [
  API_ERROR_CONFIG.REQUEST_CANCELLED.message,
  API_ERROR_CONFIG.UNAUTHORIZED.message,
  API_ERROR_CONFIG.FORBIDDEN.message,
  API_ERROR_CONFIG.NOT_FOUND.message,
  API_ERROR_CONFIG.RATE_LIMITED.message,
  API_ERROR_CONFIG.NETWORK_ERROR.message,
] as const;

const IGNORED_TRPC_MESSAGES: Record<string, true> = {
  UNAUTHORIZED: true,
  FORBIDDEN: true,
  NOT_FOUND: true,
  TOO_MANY_REQUESTS: true,
} satisfies Partial<Record<TRPCErrorCode, true>>;

interface ExceptionListEntry {
  type?: string;
  value?: string;
}

function filterExceptions(event: CaptureResult | null): CaptureResult | null {
  if (!event || event.event !== "$exception") return event;

  const exceptionList: ExceptionListEntry[] | undefined = event.properties?.$exception_list;
  if (!exceptionList?.length) return event;

  const hasIgnoredApiError = exceptionList.some(
    (ex) =>
      ex.type === "ApiError" &&
      IGNORED_EXCEPTION_MESSAGES.includes(ex.value as (typeof IGNORED_EXCEPTION_MESSAGES)[number]),
  );
  if (hasIgnoredApiError) return null;

  const hasIgnoredTRPCError = exceptionList.some(
    (ex) => ex.type === "TRPCClientError" && ex.value && IGNORED_TRPC_MESSAGES[ex.value],
  );
  if (hasIgnoredTRPCError) return null;

  return event;
}

export const posthogClient = getPosthogClient();

function getPosthogClient() {
  if (!import.meta.env.VITE_PUBLIC_POSTHOG_KEY) {
    return;
  }

  return posthog.init(
    import.meta.env.VITE_PUBLIC_POSTHOG_KEY,
    {
      api_host: import.meta.env.VITE_PUBLIC_POSTHOG_API_HOST,
      ui_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
      person_profiles: "identified_only",
      autocapture: false,
      disable_session_recording: false,
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: false,
      },
      before_send: filterExceptions,
    },
    "breakline_platform_frontend",
  );
}
